import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  Euro,
  List,
  Map as MapIcon,
  MapPin,
  Monitor,
  Search,
  Send,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import Layout from "@/components/Layout";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useUser } from "@/context/UserContext";
import api from "@/utils/api";
import { geocodeAddress, haversineKm } from "@/utils/geocode";

const SPECIALIZATIONS = [
  "Alle",
  "Schulunterricht",
  "Gericht/Jura",
  "Arzt/Medizin",
  "Psychologie/Therapie",
  "Arbeit",
  "Konferenz/Tagung",
  "Allgemein",
];

const PROFESSION_TYPES = [
  { key: "all", label: "Alle Typen" },
  { key: "Dolmetscher", label: "Dolmetscher:in" },
  { key: "Taubendolmetscher", label: "Taubendolmetscher:in" },
  { key: "Kommunikationsassistent", label: "Kommunikationsassistent:in" },
];

const EVENT_TYPES = SPECIALIZATIONS.filter((s) => s !== "Alle");
const DEFAULT_CENTER = { lat: 52.52, lng: 13.405 };

const cleanName = (name = "") => name.replace(/\s*\(von IO\)\s*$/i, "").trim();
const toIsoDate = (date = new Date()) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
};
const formatProfession = (value) => PROFESSION_TYPES.find((p) => p.key === value)?.label || "Dolmetscher:in";

function FlyTo({ center }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.flyTo([Number(center.lat), Number(center.lng)], 11, { duration: 0.8 });
  }, [center, map]);
  return null;
}

function normalizeSlot(slot) {
  if (!slot || typeof slot !== "object") return null;
  const startRaw = slot.start || slot.start_at || slot.from || slot.start_time;
  const endRaw = slot.end || slot.end_at || slot.to || slot.end_time;
  if (!startRaw || !endRaw) return null;
  const start = new Date(startRaw);
  const end = new Date(endRaw);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { start, end };
}

function hasCalendarData(interpreter) {
  return Array.isArray(interpreter.io_busy_slots);
}

function isBusyForSlot(interpreter, date, startTime, endTime) {
  if (!date || !startTime || !endTime || !hasCalendarData(interpreter)) return false;
  const wantedStart = new Date(`${date}T${startTime}:00`);
  const wantedEnd = new Date(`${date}T${endTime}:00`);
  if (Number.isNaN(wantedStart.getTime()) || Number.isNaN(wantedEnd.getTime()) || wantedEnd <= wantedStart) return false;
  return interpreter.io_busy_slots
    .map(normalizeSlot)
    .filter(Boolean)
    .some((slot) => wantedStart < slot.end && wantedEnd > slot.start);
}

function getAvailability(interpreter, date, startTime, endTime) {
  if (!date || !startTime || !endTime) {
    return { label: "Termin wählen", tone: "neutral" };
  }
  if (!hasCalendarData(interpreter)) {
    return { label: "Kalender nicht bestätigt", tone: "neutral" };
  }
  if (isBusyForSlot(interpreter, date, startTime, endTime)) {
    return { label: "Zu dieser Zeit belegt", tone: "busy" };
  }
  return {
    label: `Verfügbar ${formatDate(date)}, ${startTime}–${endTime}`,
    tone: "available",
  };
}

function durationHours(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some(Number.isNaN)) return null;
  const minutes = eh * 60 + em - (sh * 60 + sm);
  return minutes > 0 ? minutes / 60 : null;
}

function estimatedPrice(interpreter, start, end) {
  const hours = durationHours(start, end);
  const rate = Number(interpreter.hourly_rate || 0);
  if (!hours || !rate) return null;
  const billableHours = Math.max(hours, Number(interpreter.minimum_booking_hours || 0));
  const base = billableHours * rate;
  const travel = interpreter._distance_km != null && Number(interpreter.travel_cost_per_km || 0) > 0
    ? interpreter._distance_km * Number(interpreter.travel_cost_per_km)
    : 0;
  return Math.round((base + travel) * 100) / 100;
}

function buildDayEntries(from, to, start, end) {
  if (!from || !to || to < from) return [];
  const out = [];
  const cur = new Date(`${from}T12:00:00`);
  const last = new Date(`${to}T12:00:00`);
  while (cur <= last) {
    out.push({ date: toIsoDate(cur), start_time: start || "09:00", end_time: end || "17:00" });
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function RequestModal({ interpreter, broadcast, matches, prefill, filters, onClose, onSuccess }) {
  const { user } = useUser();
  const [costApprovals, setCostApprovals] = useState([]);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    event_type: filters.specialization !== "Alle" ? filters.specialization : "Allgemein",
    event_date: prefill.date || "",
    event_date_to: "",
    event_time: prefill.startTime || "",
    event_time_end: prefill.endTime || "",
    event_location: prefill.mode === "online" ? "Online" : (prefill.location || ""),
    event_description: "",
    cost_approval_id: "",
    is_multi_day: false,
    double_staffing: false,
    double_staffing_note: "",
  });

  useEffect(() => {
    api.get(`/cost-approvals/${user.id}`).then((r) => setCostApprovals(r.data)).catch(() => {});
  }, [user.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.event_date || !form.event_time || !form.event_time_end) return;
    setSending(true);
    try {
      const payload = {
        ...form,
        event_date_to: form.is_multi_day ? form.event_date_to : "",
        day_entries: form.is_multi_day
          ? buildDayEntries(form.event_date, form.event_date_to, form.event_time, form.event_time_end)
          : [],
      };
      if (broadcast) {
        await api.post(`/requests/matched-broadcast?client_id=${user.id}`, {
          ...payload,
          candidate_ids: matches.map((m) => m.user_id),
          broadcast_specializations: filters.specialization === "Alle" ? [] : [filters.specialization],
          broadcast_profession_types: filters.professionType === "all" ? [] : [filters.professionType],
        });
      } else {
        await api.post(`/requests?client_id=${user.id}`, { ...payload, interpreter_id: interpreter.user_id });
      }
      onSuccess();
    } catch (err) {
      alert(err.response?.data?.detail || "Anfrage konnte nicht gesendet werden.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[9999] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
    >
      <motion.div
        className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl"
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#0D9488]">
                {broadcast ? "Schnell-Anfrage" : "Direkte Anfrage"}
              </p>
              <h2 className="text-xl font-bold text-gray-900 mt-1">
                {broadcast ? `${matches.length} passende Dolmetscher:innen` : cleanName(interpreter?.name)}
              </h2>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100"><X size={20} /></button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Art des Einsatzes</label>
              <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#14B8A6]">
                {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
                <input type="date" min={toIsoDate()} required value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Von</label>
                <input type="time" required value={form.event_time}
                  onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bis</label>
                <input type="time" required value={form.event_time_end}
                  onChange={(e) => setForm({ ...form, event_time_end: e.target.value })}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl" />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.is_multi_day}
                onChange={(e) => setForm({ ...form, is_multi_day: e.target.checked, event_date_to: e.target.checked ? form.event_date_to : "" })}
                className="accent-[#14B8A6]" />
              Mehrtägiger Einsatz
            </label>
            {form.is_multi_day && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bis Datum</label>
                <input type="date" min={form.event_date || toIsoDate()} required value={form.event_date_to}
                  onChange={(e) => setForm({ ...form, event_date_to: e.target.value })}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl" />
                <p className="text-xs text-gray-500 mt-1">Die gewählten Uhrzeiten gelten für alle Tage.</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ort / Durchführung</label>
              {prefill.mode === "online" ? (
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">Online</div>
              ) : (
                <AddressAutocomplete
                  value={form.event_location}
                  onChange={(value) => setForm({ ...form, event_location: value })}
                  placeholder="Einsatzadresse"
                  testId="v2-request-location"
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
              <textarea required rows={3} value={form.event_description}
                onChange={(e) => setForm({ ...form, event_description: e.target.value })}
                placeholder="Was soll gedolmetscht werden?"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#14B8A6]" />
            </div>

            {costApprovals.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kostenzusage (optional)</label>
                <select value={form.cost_approval_id} onChange={(e) => setForm({ ...form, cost_approval_id: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl">
                  <option value="">Keine</option>
                  {costApprovals.map((c) => <option key={c.id} value={c.id}>{c.filename}</option>)}
                </select>
              </div>
            )}

            <div className={`rounded-xl border p-4 ${form.double_staffing ? "border-[#14B8A6] bg-[#F0FDFA]" : "border-gray-200"}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.double_staffing}
                  onChange={(e) => setForm({ ...form, double_staffing: e.target.checked })}
                  className="mt-1 accent-[#14B8A6]" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Doppelbesetzung</p>
                  <p className="text-xs text-gray-500 mt-0.5">Für lange oder anspruchsvolle Einsätze.</p>
                </div>
              </label>
              {form.double_staffing && (
                <input value={form.double_staffing_note}
                  onChange={(e) => setForm({ ...form, double_staffing_note: e.target.value })}
                  placeholder="Optionaler Hinweis"
                  className="mt-3 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              )}
            </div>

            {broadcast && (
              <div className="rounded-xl bg-[#F0FDFA] border border-[#99F6E4] p-4 text-sm text-gray-700">
                Die Anfrage wird nur an die aktuell <strong>{matches.length}</strong> passenden Treffer gesendet.
              </div>
            )}

            <button type="submit" disabled={sending || (broadcast && matches.length === 0)}
              className="w-full py-4 rounded-xl bg-[#14B8A6] text-gray-900 font-semibold hover:bg-[#0D9488] disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              <Send size={18} /> {sending ? "Wird gesendet…" : "Anfrage senden"}
            </button>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function MapSearchV2() {
  const [interpreters, setInterpreters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedSpec, setSelectedSpec] = useState("Alle");
  const [selectedProfType, setSelectedProfType] = useState("all");
  const [mode, setMode] = useState("onsite");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventCoord, setEventCoord] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [selectedInterpreter, setSelectedInterpreter] = useState(null);
  const [showDirect, setShowDirect] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [success, setSuccess] = useState(false);
  const [mobileView, setMobileView] = useState("list");

  useEffect(() => {
    api.get("/interpreters")
      .then((r) => setInterpreters(r.data || []))
      .catch(() => setInterpreters([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!eventLocation || eventCoord || mode === "online") return;
    const timer = setTimeout(() => {
      geocodeAddress(eventLocation).then((coord) => {
        if (coord) {
          setEventCoord(coord);
          setMapCenter(coord);
        }
      }).catch(() => {});
    }, 700);
    return () => clearTimeout(timer);
  }, [eventLocation, eventCoord, mode]);

  const chooseMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const coord = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setMode("onsite");
      setEventCoord(coord);
      setEventLocation("Mein Standort");
      setMapCenter(coord);
    });
  };

  const filtered = useMemo(() => {
    return interpreters
      .map((interpreter) => {
        if (!eventCoord || mode === "online") return interpreter;
        if (interpreter.service_center_lat == null || interpreter.service_center_lng == null) return null;
        const distance = haversineKm(
          { lat: interpreter.service_center_lat, lng: interpreter.service_center_lng },
          eventCoord,
        );
        if (distance == null || distance > Number(interpreter.service_radius_km || 0)) return null;
        return { ...interpreter, _distance_km: distance };
      })
      .filter(Boolean)
      .filter((i) => {
        if (mode === "online") {
          const modes = (i.delivery_modes || []).map((m) => String(m).toLowerCase());
          if (modes.length && !modes.some((m) => m.includes("online"))) return false;
        }
        if (selectedProfType !== "all" && (i.profession_type || "Dolmetscher") !== selectedProfType) return false;
        if (selectedSpec !== "Alle" && !(i.specializations || []).includes(selectedSpec)) return false;
        const q = search.trim().toLowerCase();
        if (q && ![i.name, i.bio, ...(i.languages || []), ...(i.specializations || [])].filter(Boolean).join(" ").toLowerCase().includes(q)) return false;
        if (date && startTime && endTime && isBusyForSlot(i, date, startTime, endTime)) return false;
        return true;
      })
      .sort((a, b) => (a._distance_km ?? 9999) - (b._distance_km ?? 9999));
  }, [interpreters, eventCoord, mode, selectedProfType, selectedSpec, search, date, startTime, endTime]);

  const allSearchDataPresent = mode === "online"
    ? Boolean(date && startTime && endTime)
    : Boolean(eventLocation && date && startTime && endTime);

  const prefill = { location: eventLocation, date, startTime, endTime, mode };
  const filters = { specialization: selectedSpec, professionType: selectedProfType };

  const handleSuccess = () => {
    setShowDirect(false);
    setShowBroadcast(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <Layout>
      <div className="flex flex-col md:flex-row h-[calc(100vh-74px)] relative bg-white">
        <div className="md:hidden sticky top-0 z-[1100] bg-white border-b border-gray-200 px-3 py-2 flex gap-2">
          <button onClick={() => setMobileView("list")}
            className={`flex-1 py-2 rounded-xl text-sm font-medium flex justify-center items-center gap-2 ${mobileView === "list" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}>
            <List size={16} /> Liste
          </button>
          <button onClick={() => setMobileView("map")}
            className={`flex-1 py-2 rounded-xl text-sm font-medium flex justify-center items-center gap-2 ${mobileView === "map" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}>
            <MapIcon size={16} /> Karte
          </button>
        </div>

        <aside className={`${mobileView === "list" ? "flex" : "hidden"} md:flex w-full md:w-[40%] md:min-w-[440px] md:max-w-[620px] border-r border-gray-200 bg-white flex-col`}>
          <div className="p-4 sm:p-5 border-b border-gray-100 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Dolmetscher:innen finden</h1>
                <p className="text-xs text-gray-500 mt-0.5">Termin eingeben und direkt passende Personen sehen.</p>
              </div>
              <span className="text-xs font-semibold bg-[#F0FDFA] text-[#0D9488] px-2.5 py-1 rounded-full">{filtered.length} Treffer</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setMode("onsite")}
                className={`py-2.5 rounded-xl text-sm font-medium border flex items-center justify-center gap-2 ${mode === "onsite" ? "bg-[#14B8A6] border-[#14B8A6] text-gray-900" : "border-gray-200 text-gray-600"}`}>
                <MapPin size={15} /> Vor Ort
              </button>
              <button onClick={() => { setMode("online"); setEventCoord(null); }}
                className={`py-2.5 rounded-xl text-sm font-medium border flex items-center justify-center gap-2 ${mode === "online" ? "bg-[#14B8A6] border-[#14B8A6] text-gray-900" : "border-gray-200 text-gray-600"}`}>
                <Monitor size={15} /> Online
              </button>
            </div>

            {mode === "onsite" && (
              <AddressAutocomplete
                value={eventLocation}
                onChange={(value) => { setEventLocation(value); setEventCoord(null); }}
                onPick={({ label, lat, lng }) => {
                  setEventLocation(label);
                  const coord = { lat, lng };
                  setEventCoord(coord);
                  setMapCenter(coord);
                }}
                placeholder="Einsatzort eingeben"
                testId="v2-event-location"
              />
            )}

            <div className="grid grid-cols-3 gap-2">
              <div className="relative">
                <CalendarDays size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input type="date" min={toIsoDate()} value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full pl-9 pr-2 py-2.5 border border-gray-200 rounded-xl text-sm" />
              </div>
              <div className="relative">
                <Clock3 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                  className="w-full pl-9 pr-2 py-2.5 border border-gray-200 rounded-xl text-sm" />
              </div>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm" />
            </div>

            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, Sprache oder Fähigkeit…"
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6]" />
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={chooseMyLocation} className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">In meiner Nähe</button>
              <button onClick={() => setMode("online")} className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">Online verfügbar</button>
              <button onClick={() => setDate(toIsoDate())} className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">Heute verfügbar</button>
            </div>

            <button onClick={() => setShowBroadcast(true)} disabled={!allSearchDataPresent || filtered.length === 0}
              className="w-full rounded-2xl bg-[#F0FDFA] border border-[#5EEAD4] px-4 py-3 text-left hover:bg-[#CCFBF1] disabled:opacity-50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#14B8A6] flex items-center justify-center shrink-0"><Sparkles size={18} /></div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900 text-sm">Schnell-Anfrage</p>
                  <p className="text-xs text-gray-600 mt-0.5">Eine Anfrage senden – {filtered.length || "passende"} Treffer können den Termin übernehmen.</p>
                </div>
                <Send size={16} className="text-[#0D9488] mt-1" />
              </div>
            </button>

            <div className="flex flex-wrap gap-1.5">
              {PROFESSION_TYPES.map((pt) => (
                <button key={pt.key} onClick={() => setSelectedProfType(pt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${selectedProfType === pt.key ? "bg-[#0D9488] text-white" : "bg-gray-100 text-gray-600"}`}>
                  {pt.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-1.5">
              {SPECIALIZATIONS.map((spec) => (
                <button key={spec} onClick={() => setSelectedSpec(spec)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium ${selectedSpec === spec ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}>
                  {spec}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {loading ? (
              <div className="text-center py-10 text-gray-400 text-sm">Suche läuft…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm">Keine passenden Dolmetscher:innen gefunden.</div>
            ) : filtered.map((interpreter) => {
              const availability = getAvailability(interpreter, date, startTime, endTime);
              const estimate = estimatedPrice(interpreter, startTime, endTime);
              const selected = selectedInterpreter?.user_id === interpreter.user_id;
              const modeLabels = interpreter.delivery_modes?.length ? interpreter.delivery_modes : ["Vor Ort"];
              return (
                <motion.div key={interpreter.user_id} layout
                  onClick={() => setSelectedInterpreter(selected ? null : interpreter)}
                  className={`rounded-2xl border bg-white p-4 cursor-pointer transition-all ${selected ? "border-[#14B8A6] shadow-md" : "border-gray-200 hover:shadow-sm"}`}>
                  <div className="flex gap-3">
                    <div className="w-11 h-11 rounded-xl bg-[#FEF3CE] flex items-center justify-center text-xl shrink-0">🤟</div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-bold text-gray-900 truncate">{cleanName(interpreter.name)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {interpreter.service_city || "Einsatzgebiet"}{interpreter._distance_km != null ? ` · ${interpreter._distance_km.toFixed(1)} km entfernt` : ""}
                          </p>
                        </div>
                        <span className={`shrink-0 text-[11px] px-2 py-1 rounded-full font-medium ${availability.tone === "available" ? "bg-green-100 text-green-700" : availability.tone === "busy" ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"}`}>
                          {availability.label}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-[11px] px-2 py-1 rounded-full bg-[#E6FFFB] text-[#0D9488]">{formatProfession(interpreter.profession_type || "Dolmetscher")}</span>
                        {(interpreter.languages || []).slice(0, 2).map((language) => (
                          <span key={language} className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-600">{language}</span>
                        ))}
                        {modeLabels.map((label) => (
                          <span key={label} className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-600">{label}</span>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(interpreter.specializations || []).slice(0, 4).map((spec) => (
                          <span key={spec} className="text-[11px] px-2 py-1 rounded-full bg-[#FEF3CE] text-[#CA8A04]">{spec}</span>
                        ))}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-600">
                        {Number(interpreter.hourly_rate || 0) > 0 && <span className="flex items-center gap-1"><Euro size={13} /> {interpreter.hourly_rate} €/Std.</span>}
                        {estimate != null && <span className="font-semibold text-gray-900">ca. {estimate.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € gesamt</span>}
                        {interpreter.completed_jobs > 0 && <span className="flex items-center gap-1"><Users size={13} /> {interpreter.completed_jobs} Einsätze</span>}
                        {interpreter.average_response_minutes > 0 && <span className="flex items-center gap-1"><Clock3 size={13} /> ~{interpreter.average_response_minutes} Min. Antwortzeit</span>}
                      </div>
                    </div>
                  </div>

                  {selected && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-4 pt-4 border-t border-gray-100">
                      {interpreter.bio && <p className="text-sm text-gray-600 leading-relaxed">{interpreter.bio}</p>}
                      {Number(interpreter.hourly_rate || 0) > 0 && (
                        <div className="mt-3 rounded-xl bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
                          <p><strong>Preis:</strong> {interpreter.hourly_rate} €/Std.</p>
                          {interpreter.minimum_booking_hours > 0 && <p><strong>Mindestbuchung:</strong> {interpreter.minimum_booking_hours} Std.</p>}
                          {interpreter.travel_cost_per_km > 0 && <p><strong>Fahrtkosten:</strong> {interpreter.travel_cost_per_km} €/km</p>}
                          {interpreter.vat_note && <p><strong>USt.:</strong> {interpreter.vat_note}</p>}
                          {estimate != null && <p className="font-semibold text-gray-900"><strong>Geschätzter Gesamtpreis:</strong> {estimate.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>}
                        </div>
                      )}
                      <div className="flex gap-2 mt-3">
                        <a href={`mailto:${interpreter.email}`} onClick={(e) => e.stopPropagation()}
                          className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium text-center">E-Mail</a>
                        <button onClick={(e) => { e.stopPropagation(); setShowDirect(true); }}
                          className="flex-1 py-2.5 rounded-xl bg-[#14B8A6] text-gray-900 text-sm font-semibold hover:bg-[#0D9488]">
                          Anfrage senden
                        </button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </aside>

        <main className={`${mobileView === "map" ? "block" : "hidden"} md:block flex-1 relative`}>
          {success && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1200] px-5 py-3 rounded-2xl bg-green-600 text-white shadow-lg text-sm font-semibold flex items-center gap-2">
              <CheckCircle2 size={17} /> Anfrage erfolgreich gesendet
            </div>
          )}
          <MapContainer center={[mapCenter.lat, mapCenter.lng]} zoom={11} style={{ height: "100%", width: "100%" }}>
            <FlyTo center={mapCenter} />
            <TileLayer
              url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap-Mitwirkende'
              maxZoom={18}
            />
            {eventCoord && mode === "onsite" && (
              <Marker position={[eventCoord.lat, eventCoord.lng]}>
                <Popup><strong>Einsatzort</strong><br />{eventLocation}</Popup>
              </Marker>
            )}
            {filtered.map((interpreter) => {
              if (interpreter.service_center_lat == null || interpreter.service_center_lng == null) return null;
              const icon = L.divIcon({
                className: "",
                html: '<div style="width:30px;height:30px;border-radius:50%;background:#14B8A6;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;font-size:14px">🤟</div>',
                iconSize: [30, 30], iconAnchor: [15, 15],
              });
              return (
                <Marker key={interpreter.user_id} position={[interpreter.service_center_lat, interpreter.service_center_lng]} icon={icon}>
                  <Popup>
                    <strong>{cleanName(interpreter.name)}</strong><br />
                    {formatProfession(interpreter.profession_type || "Dolmetscher")}
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </main>
      </div>

      <AnimatePresence>
        {showDirect && selectedInterpreter && (
          <RequestModal interpreter={selectedInterpreter} prefill={prefill} filters={filters} onClose={() => setShowDirect(false)} onSuccess={handleSuccess} />
        )}
        {showBroadcast && (
          <RequestModal broadcast matches={filtered} prefill={prefill} filters={filters} onClose={() => setShowBroadcast(false)} onSuccess={handleSuccess} />
        )}
      </AnimatePresence>
    </Layout>
  );
}
